import { useEffect, useMemo, useState } from "react";
import { api } from "../api/axios";
import { useI18n } from "../../i18n/I18nContext";

function SkeletonRow({ cols = 5 }) {
  return (
    <tr className="border-t border-gray-100">
      {Array.from({ length: cols }).map((_, i) => (
        <td key={i} className="p-4">
          <div className="h-4 w-full max-w-[220px] animate-pulse rounded-full bg-gray-200" />
        </td>
      ))}
    </tr>
  );
}

function RoleBadge({ name }) {
  const { t } = useI18n();
  const isAdmin = name === "admin";

  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${
        isAdmin
          ? "bg-purple-50 text-purple-700 ring-1 ring-purple-200"
          : "bg-blue-50 text-blue-700 ring-1 ring-blue-200"
      }`}
    >
      {isAdmin ? t("Admin") : t("Cliente")}
    </span>
  );
}

export default function Dashboard() {
  const { t } = useI18n();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);

  const itemsPerPage = 10;
  const totalPages = Math.ceil(users.length / itemsPerPage);

  const paginatedUsers = users.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const usersCountLabel = useMemo(() => {
    if (loading) return t("A carregar…");

    return `${users.length} utilizador${users.length === 1 ? "" : "es"}`;
  }, [users.length, loading, t]);

  const loadData = async () => {
    setLoading(true);
    setErrorMsg("");

    try {
      const [rolesRes, usersRes] = await Promise.all([
        api.get("/api/admin/roles"),
        api.get("/api/admin/users"),
      ]);

      setRoles(rolesRes.data || []);
      setUsers(usersRes.data?.data || usersRes.data || []);
    } catch (err) {
      setErrorMsg(err?.response?.data?.message || t("Erro a carregar dados."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const openEdit = (user) => {
    setErrorMsg("");

    const userRoles = (user.roles || []).map((role) => role.name);

    setSelected({
      id: user.id,
      name: user.name || "",
      email: user.email || "",
      roles: userRoles.length > 0 ? [userRoles[0]] : [],
      password: "",
    });
  };

  const closeEdit = () => {
    if (saving) return;

    setSelected(null);
    setErrorMsg("");
  };

  const selectRole = (roleName) => {
    setSelected((prev) => {
      if (!prev) return prev;

      return {
        ...prev,
        roles: [roleName],
      };
    });
  };

  const saveUser = async () => {
    if (!selected) return;

    if (!selected.name.trim()) {
      setErrorMsg(t("O nome é obrigatório."));
      return;
    }

    if (!selected.email.trim()) {
      setErrorMsg(t("O email é obrigatório."));
      return;
    }

    if (selected.roles.length !== 1) {
      setErrorMsg(t("Seleciona exatamente uma role: admin ou client."));
      return;
    }

    setSaving(true);
    setErrorMsg("");

    try {
      const payload = {
        name: selected.name.trim(),
        email: selected.email.trim(),
        roles: selected.roles,
      };

      if (selected.password?.trim()) {
        payload.password = selected.password.trim();
      }

      const res = await api.put(`/api/admin/users/${selected.id}`, payload);

      setUsers((prev) =>
        prev.map((user) => (user.id === selected.id ? res.data : user))
      );

      setSelected(null);
    } catch (err) {
      const data = err?.response?.data;

      if (data?.errors) {
        const firstKey = Object.keys(data.errors)[0];
        setErrorMsg(data.errors[firstKey]?.[0] || t("Erro de validação."));
      } else {
        setErrorMsg(data?.message || t("Erro a guardar utilizador."));
      }
    } finally {
      setSaving(false);
    }
  };

  const deleteUser = async (id) => {
    const user = users.find((item) => item.id === id);
    const label = user ? `${user.name} (${user.email})` : `ID ${id}`;

    if (!window.confirm(`${t("Queres mesmo apagar o utilizador")} ${label}?`)) return;

    setErrorMsg("");

    try {
      await api.delete(`/api/admin/users/${id}`);

      setUsers((prev) => prev.filter((user) => user.id !== id));

      if (selected?.id === id) {
        setSelected(null);
      }
    } catch (err) {
      setErrorMsg(err?.response?.data?.message || t("Erro a apagar utilizador."));
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-slate-100 px-6 py-10 pt-24">
      <div className="mx-auto max-w-6xl">
        <div className="mb-8 flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
          <div>
            <div className="mb-2 inline-flex rounded-full bg-gray-900 px-3 py-1 text-xs font-semibold text-white shadow-sm">
              {t("Painel de administração")}
            </div>

            <h1 className="text-3xl font-bold tracking-tight text-gray-950">
              {t("Utilizadores")}
            </h1>

            <p className="mt-2 text-sm text-gray-600">
              {t("Gere os utilizadores, permissões e acessos da plataforma.")}
            </p>

            <p className="mt-1 text-sm font-medium text-gray-700">
              {usersCountLabel}
            </p>
          </div>

          <button
            onClick={loadData}
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 shadow-sm transition hover:-translate-y-0.5 hover:bg-gray-50 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-gray-900/20 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0"
            disabled={loading}
            title={t("Recarregar")}
          >
            <span className={loading ? "animate-spin" : ""}>⟳</span>
            {t("Recarregar")}
          </button>
        </div>

        {errorMsg && (
          <div className="mb-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700 shadow-sm">
            {errorMsg}
          </div>
        )}

        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
          <div className="border-b border-gray-200 bg-white px-5 py-4">
            <div className="flex items-center justify-between gap-4">
              <div>
                <span className="text-sm font-bold text-gray-950">
                  {t("Lista de utilizadores")}
                </span>

                <p className="mt-0.5 text-xs text-gray-500">
                  {t("Visualiza, edita ou remove utilizadores existentes.")}
                </p>
              </div>

              <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600">
                {loading ? t("A obter dados…") : t("Atualizado")}
              </span>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs font-bold uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="p-4">ID</th>
                  <th className="p-4">{t("Nome")}</th>
                  <th className="p-4">{t("Email")}</th>
                  <th className="p-4">{t("Role")}</th>
                  <th className="p-4 text-right">{t("Ações")}</th>
                </tr>
              </thead>

              <tbody className="text-gray-800">
                {loading ? (
                  Array.from({ length: 7 }).map((_, i) => (
                    <SkeletonRow key={i} cols={5} />
                  ))
                ) : (
                  <>
                    {paginatedUsers.map((user) => (
                      <tr
                        key={user.id}
                        className="border-t border-gray-100 transition hover:bg-gray-50/80"
                      >
                        <td className="p-4 font-bold text-gray-950">
                          #{user.id}
                        </td>

                        <td className="p-4">
                          <div className="font-semibold text-gray-900">
                            {user.name}
                          </div>
                        </td>

                        <td className="p-4 text-gray-600">{user.email}</td>

                        <td className="p-4">
                          <div className="flex flex-wrap gap-2">
                            {(user.roles || []).length ? (
                              (user.roles || []).map((role) => (
                                <RoleBadge
                                  key={role.id ?? role.name}
                                  name={role.name}
                                />
                              ))
                            ) : (
                              <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-500">
                                {t("Sem role")}
                              </span>
                            )}
                          </div>
                        </td>

                        <td className="p-4">
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => openEdit(user)}
                              className="rounded-xl bg-gray-950 px-3.5 py-2 text-xs font-bold text-white shadow-sm transition hover:-translate-y-0.5 hover:bg-gray-800 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-gray-900/20"
                            >
                              {t("Editar")}
                            </button>

                            <button
                              onClick={() => deleteUser(user.id)}
                              className="rounded-xl border border-red-200 bg-white px-3.5 py-2 text-xs font-bold text-red-700 shadow-sm transition hover:-translate-y-0.5 hover:bg-red-50 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-red-600/20"
                            >
                              {t("Apagar")}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}

                    {users.length === 0 && (
                      <tr>
                        <td className="p-12 text-center" colSpan={5}>
                          <div className="mx-auto max-w-md">
                            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-gray-100 text-2xl">
                              👥
                            </div>

                            <div className="text-lg font-bold text-gray-950">
                              {t("Sem utilizadores")}
                            </div>

                            <div className="mt-1 text-sm text-gray-600">
                              {t("Ainda não há registos para mostrar.")}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                )}
              </tbody>
            </table>

            {!loading && users.length > itemsPerPage && (
              <div className="flex flex-col items-center justify-between gap-3 border-t border-gray-100 px-5 py-4 sm:flex-row">
                <span className="text-sm text-gray-500">
                  {t("Página")}{" "}
                  <span className="font-bold text-gray-900">
                    {currentPage}
                  </span>{" "}
                  {t("de")}{" "}
                  <span className="font-bold text-gray-900">{totalPages}</span>
                </span>

                <div className="flex items-center gap-2">
                  <button
                    className="rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                    onClick={() =>
                      setCurrentPage((prev) => Math.max(prev - 1, 1))
                    }
                    disabled={currentPage === 1}
                  >
                    {t("Anterior")}
                  </button>

                  <button
                    className="rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                    onClick={() =>
                      setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                    }
                    disabled={currentPage === totalPages}
                  >
                    {t("Próximo")}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {selected && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-950/60 px-6 backdrop-blur-sm">
            <div className="w-full max-w-xl overflow-hidden rounded-3xl bg-white shadow-2xl ring-1 ring-black/5">
              <div className="bg-gradient-to-r from-gray-950 to-gray-800 px-6 py-5 text-white">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="mb-2 inline-flex rounded-full bg-white/10 px-3 py-1 text-xs font-semibold text-white ring-1 ring-white/15">
                      {t("Editar utilizador")}
                    </div>

                    <h2 className="text-xl font-bold">
                      {selected.name || t("Utilizador")}
                    </h2>

                    <p className="mt-1 text-sm text-gray-300">
                      ID #{selected.id} · {selected.email}
                    </p>
                  </div>

                  <button
                    onClick={closeEdit}
                    className="rounded-xl bg-white/10 px-3 py-2 text-sm font-bold text-white transition hover:bg-white/20 disabled:opacity-50"
                    disabled={saving}
                  >
                    ✕
                  </button>
                </div>
              </div>

              <div className="space-y-5 px-6 py-6">
                <div>
                  <label className="mb-1.5 block text-sm font-bold text-gray-800">
                    {t("Nome")}
                  </label>

                  <input
                    value={selected.name}
                    onChange={(e) =>
                      setSelected((prev) => ({
                        ...prev,
                        name: e.target.value,
                      }))
                    }
                    className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm font-medium text-gray-900 outline-none transition placeholder:text-gray-400 focus:border-gray-400 focus:bg-white focus:ring-4 focus:ring-gray-900/5"
                    placeholder={t("Nome do utilizador")}
                  />
                </div>

                <div>
                  <label className="mb-1.5 block text-sm font-bold text-gray-800">
                    {t("Email")}
                  </label>

                  <input
                    type="email"
                    value={selected.email}
                    onChange={(e) =>
                      setSelected((prev) => ({
                        ...prev,
                        email: e.target.value,
                      }))
                    }
                    className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm font-medium text-gray-900 outline-none transition placeholder:text-gray-400 focus:border-gray-400 focus:bg-white focus:ring-4 focus:ring-gray-900/5"
                    placeholder={t("email@exemplo.com")}
                  />
                </div>

                <div>
                  <label className="mb-1.5 block text-sm font-bold text-gray-800">
                    {t("Nova password")}
                  </label>

                  <input
                    type="password"
                    value={selected.password}
                    onChange={(e) =>
                      setSelected((prev) => ({
                        ...prev,
                        password: e.target.value,
                      }))
                    }
                    className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm font-medium text-gray-900 outline-none transition placeholder:text-gray-400 focus:border-gray-400 focus:bg-white focus:ring-4 focus:ring-gray-900/5"
                    placeholder={t("Deixar vazio para não alterar")}
                  />

                  <p className="mt-1.5 text-xs font-medium text-gray-500">
                    {t("Só será alterada se preencheres este campo.")}
                  </p>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-bold text-gray-800">
                    {t("Role")}
                  </label>

                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {roles.length > 0 ? (
                      roles.map((role) => {
                        const isSelected = selected.roles.includes(role.name);
                        const isAdmin = role.name === "admin";

                        return (
                          <button
                            key={role.id ?? role.name}
                            type="button"
                            onClick={() => selectRole(role.name)}
                            className={`rounded-2xl border p-4 text-left transition ${
                              isSelected
                                ? "border-gray-950 bg-gray-950 text-white shadow-lg shadow-gray-900/20"
                                : "border-gray-200 bg-white text-gray-800 hover:-translate-y-0.5 hover:border-gray-300 hover:shadow-md"
                            }`}
                          >
                              <div className="flex items-center justify-between gap-3">
                                <div>
                                  <div className="text-sm font-bold">
                                    {isAdmin ? t("Administrador") : t("Cliente")}
                                  </div>

                                  <div
                                    className={`mt-1 text-xs ${
                                      isSelected
                                        ? "text-gray-300"
                                        : "text-gray-500"
                                    }`}
                                  >
                                    {isAdmin
                                      ? t("Acesso ao painel de administração.")
                                      : t("Acesso normal de cliente.")}
                                  </div>
                                </div>

                              <div
                                className={`flex h-6 w-6 items-center justify-center rounded-full border text-xs font-bold ${
                                  isSelected
                                    ? "border-white bg-white text-gray-950"
                                    : "border-gray-300 text-transparent"
                                }`}
                              >
                                ✓
                              </div>
                            </div>
                          </button>
                        );
                      })
                    ) : (
                      <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm font-medium text-gray-500 sm:col-span-2">
                        {t("Não existem roles disponíveis.")}
                      </div>
                    )}
                  </div>

                  <p className="mt-2 text-xs font-medium text-gray-500">
                    {t("Apenas uma role pode estar ativa: admin ou client.")}
                  </p>
                </div>
              </div>

              <div className="flex flex-col-reverse gap-3 border-t border-gray-100 bg-gray-50 px-6 py-5 sm:flex-row sm:justify-end">
                <button
                  onClick={closeEdit}
                  className="rounded-xl border border-gray-200 bg-white px-5 py-2.5 text-sm font-bold text-gray-700 shadow-sm transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
                  disabled={saving}
                >
                  {t("Cancelar")}
                </button>

                <button
                  onClick={saveUser}
                  className="rounded-xl bg-gray-950 px-5 py-2.5 text-sm font-bold text-white shadow-sm transition hover:-translate-y-0.5 hover:bg-gray-800 hover:shadow-md disabled:cursor-not-allowed disabled:translate-y-0 disabled:opacity-60"
                  disabled={saving}
                >
                  {saving ? t("A guardar...") : t("Guardar alterações")}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}